import type { MapGeoJSONFeature, MapMouseEvent } from 'maplibre-gl';
import { useEffect } from 'react';
import { useInteractiveLayers, useMap } from './mapContext';
import { useMapEvent } from './useMapEvent';

/**
 * Clicks on the map itself; clicks on interactive layers (see useLayerClick) are left to those, and
 * clicks that only dismiss something (see useDismissOnMapClick) are ignored.
 */
export function useMapClick(handler: (e: MapMouseEvent) => void) {
  const layers = useInteractiveLayers();
  useMapEvent('click', (e) => {
    if (!layers.mapClicksClaimed() && !layers.featureAt(e.target, e.point)) handler(e);
  });
}

/**
 * While `open`, a click on the map (not on an interactive layer) calls `onDismiss` and nothing else:
 * tapping away from a popup should not also, say, set a takeoff.
 */
export function useDismissOnMapClick(open: boolean, onDismiss: () => void) {
  const layers = useInteractiveLayers();
  useEffect(() => (open ? layers.claimMapClicks() : undefined), [layers, open]);
  useMapEvent('click', (e) => {
    if (open && !layers.featureAt(e.target, e.point)) onDismiss();
  });
}

/**
 * Clicks on features of the given layers, which then do not reach useMapClick handlers.
 * A mouse over them shows the pointer cursor.
 */
export function useLayerClick(
  ids: readonly string[],
  handler: (feature: MapGeoJSONFeature, e: MapMouseEvent) => void,
) {
  const map = useMap();
  const layers = useInteractiveLayers();
  const key = ids.join('\n');

  useEffect(() => layers.register(key.split('\n')), [layers, key]);

  useMapEvent('click', (e) => {
    const feature = layers.featureAt(e.target, e.point, ids);
    if (feature) handler(feature, e);
  });

  // The container's cursor, so it does not fight layers that set the canvas cursor (e.g. a crosshair).
  useMapEvent('mousemove', (e) => {
    const hit = !!layers.featureAt(e.target, e.point, ids);
    e.target.getCanvasContainer().style.cursor = hit ? 'pointer' : '';
  });
  useEffect(() => () => void map?.getCanvasContainer().style.removeProperty('cursor'), [map]);
}
